import React, { FC, forwardRef, memo, ReactNode, RefAttributes, useImperativeHandle, useRef } from "react";
import { Container, ContentContainer, HeaderContainer, ContentWrapper } from "./stylesV2";
import { useElementDynamicHeight } from "@core-ui/hooks/UseElementDynamicHeight";

interface CoreLayoutWithProperties extends FC<CoreLayoutProps> {
    Header: React.FunctionComponent<CoreLayoutHeaderProps>;
    Content: React.FunctionComponent<CoreLayoutContentProps & RefAttributes<HTMLDivElement>>;
}

export interface CoreLayoutProps {
    children: ReactNode;
    containerStyleProps?: {
        backgroundColor?: string;
    };
}

export interface CoreLayoutContentProps {
    children: ReactNode;
    contentId?: string;
    headerRef: React.RefObject<HTMLDivElement>;
    containerStyleProps?: {
        backgroundColor?: string;
        borderRadius?: string;
        extraBottomPadding?: number;
        marginTop?: number;
    };
    wrapperStyleProps?: {
        backgroundColor?: string;
    };
}

export interface CoreLayoutHeaderProps {
    children: ReactNode;
    containerStyleProps?: {
        backgroundColor?: string;
        background?: string;
        boxShadow?: string;
        zIndex?: number;
    };
    containerRef: React.RefObject<HTMLDivElement>;
    dataTestId?: string;
}

const CoreLayout: CoreLayoutWithProperties = function CoreLayout({
    containerStyleProps = {},
    children,
}: CoreLayoutProps): React.ReactElement {
    return <Container {...containerStyleProps}>{children}</Container>;
} as CoreLayoutWithProperties;

CoreLayout.Header = memo(function CoreLayoutHeader({
    containerStyleProps = {},
    containerRef,
    dataTestId,
    children,
}: CoreLayoutHeaderProps): React.ReactElement {
    return (
        <HeaderContainer ref={containerRef} data-testid={dataTestId} {...containerStyleProps}>
            {children}
        </HeaderContainer>
    );
});

CoreLayout.Content = forwardRef<HTMLDivElement, CoreLayoutContentProps>(function CoreLayoutContent(
    props: CoreLayoutContentProps,
    ref,
): React.ReactElement {
    const { contentId, headerRef, containerStyleProps = {}, children, wrapperStyleProps = {} } = props;
    const divRef = useRef<HTMLDivElement>(null);
    const { elementHeight: headerHeight } = useElementDynamicHeight(headerRef);

    useImperativeHandle(ref, () => ({
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        scrollTo: (x: number, y: number): void => {
            divRef.current?.scrollTo(x, y);
        },
        getScrollPosition: (): number => {
            return divRef?.current?.scrollTop ?? 0;
        },
        addEventListener: (eventName: string, fn: EventListenerOrEventListenerObject, options?: boolean): void => {
            divRef.current?.addEventListener(eventName, fn, options);
        },
        removeEventListener: (eventName: string, fn: EventListenerOrEventListenerObject, options?: boolean): void => {
            divRef.current?.removeEventListener(eventName, fn, options);
        },
    }));

    return (
        <ContentWrapper headerHeight={headerHeight} {...wrapperStyleProps}>
            <ContentContainer id={contentId} ref={divRef} {...containerStyleProps}>
                {children}
            </ContentContainer>
        </ContentWrapper>
    );
});

if (__DEV__) CoreLayout.Content.displayName = "CoreLayoutContent";

export default CoreLayout;
